/**
 * SalesReportParamsModal — parametry zestawienia „Sprzedaż za okres", jak okno przed raportem
 * w Symfonii: najpierw mówisz, ZA CO liczyć, dopiero potem dostajesz raport.
 *
 * Czego tu świadomie NIE ma: filtrów po typie dokumentu, serii i dziale. Te wartości zawęża się
 * na gotowym raporcie — klikając w komórkę albo wybierając z listy tego, co w zakresie faktycznie
 * wystąpiło. Wypisywanie ich w oknie parametrów wymagałoby słownika, do którego potrzeba prawa
 * WYSTAWIANIA dokumentów; użytkownik z samym podglądem zobaczyłby puste listy.
 *
 * Wymiary liczone ze szczegółów dokumentów (rejestr pieniężny, towar, stawka VAT) są wyszarzone,
 * dopóki nie włączy się dociągania szczegółów — bo bez niego raport po prostu nie ma tych danych.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HM_SALES_REPORT_DIMENSIONS,
  type HmContractorListItem,
  type HmListResponse,
  hmReportDimensionNeedsDetails,
} from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { DatePicker } from '@/components/DatePicker';
import { Dropdown } from '@/components/Dropdown';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';
import type { SalesReportParams } from '@/lib/handel-magazyn/reports-api';

import { rangeFor, type RangePreset } from './HmListShell';

/** Kolejność jak w oknie raportu Symfonii — od najkrótszych okresów do „wszystkie daty". */
const MODAL_PRESETS: RangePreset[] = [
  'today',
  'yesterday',
  'thisMonth',
  'thisQuarter',
  'thisYear',
  'prevMonth',
  'last30',
  'last60',
  'last90',
  'all',
];

export function defaultReportParams(): SalesReportParams {
  const range = rangeFor('thisMonth');
  return {
    preset: 'thisMonth',
    dateFrom: range.from,
    dateTo: range.to,
    buyerId: null,
    typeCode: null,
    departmentId: null,
    series: null,
    status: null,
    dim: 'none',
    positions: false,
    details: false,
    ids: [],
  };
}

export function SalesReportParamsModal({
  open,
  companyId,
  initial,
  title,
  onClose,
  onSubmit,
}: {
  open: boolean;
  companyId: string | null;
  initial?: SalesReportParams;
  /** Nazwa raportu — okno jest wspólne dla wszystkich zestawień sprzedaży. */
  title?: string;
  onClose: () => void;
  onSubmit: (params: SalesReportParams) => void;
}) {
  const { t } = useTranslation();
  const [params, setParams] = useState<SalesReportParams>(initial ?? defaultReportParams());

  // Każde otwarcie zaczyna od aktualnych parametrów raportu — inaczej okno pokazywałoby stan
  // sprzed poprzedniego uruchomienia, mimo że raport już go nie używa.
  useEffect(() => {
    if (open) setParams(initial ?? defaultReportParams());
  }, [open, initial]);

  const contractors = useQuery({
    queryKey: ['handel-magazyn', 'contractors', companyId],
    enabled: open && !!companyId,
    queryFn: async () =>
      (
        await api.get<HmListResponse<HmContractorListItem>>(
          `/handel-magazyn/contractors?companyId=${companyId}`,
        )
      ).data,
  });

  function set(patch: Partial<SalesReportParams>) {
    setParams((prev) => ({ ...prev, ...patch }));
  }

  function applyPreset(preset: RangePreset) {
    if (preset === 'custom') {
      set({ preset });
      return;
    }
    const range = rangeFor(preset);
    set({ preset, dateFrom: range.from, dateTo: range.to });
  }

  const dimensionOptions = useMemo(
    () =>
      HM_SALES_REPORT_DIMENSIONS.map((dim) => ({
        value: dim,
        label: t(`tenant.handelMagazyn.reports.dimension.${dim}`),
        disabled: hmReportDimensionNeedsDetails(dim) && !params.details,
      })),
    [t, params.details],
  );

  function toggleDetails(next: boolean) {
    // Wyłączenie dociągania zabiera dane wymiarowi, który na nich stoi — wtedy wracamy do
    // raportu płaskiego, zamiast zostawiać wybór, którego raport nie umie policzyć.
    const dimLosesData = hmReportDimensionNeedsDetails(params.dim) && !next;
    set({
      details: next,
      ...(dimLosesData ? { dim: 'none' as const } : {}),
      ...(next ? {} : { positions: false }),
    });
  }

  const canSubmit =
    !!companyId && !!params.dateFrom && !!params.dateTo && params.dateFrom <= params.dateTo;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title ?? t('tenant.handelMagazyn.reports.salesByPeriod.title')}
      description={t('tenant.handelMagazyn.reports.salesByPeriod.subtitle')}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            onClick={() => onSubmit(params)}
            data-testid="hm-report-params-submit"
          >
            {t('tenant.handelMagazyn.reports.run')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <section>
          <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
            {t('tenant.handelMagazyn.reports.period')}
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {MODAL_PRESETS.map((preset) => (
              <Button
                key={preset}
                variant={params.preset === preset ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => applyPreset(preset)}
                data-testid={`hm-report-preset-${preset}`}
              >
                {t(`tenant.handelMagazyn.filters.preset.${preset}`)}
              </Button>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <DatePicker
              value={params.dateFrom}
              onChange={(value) => set({ preset: 'custom', dateFrom: value })}
              aria-label={t('tenant.handelMagazyn.filters.dateFrom')}
              data-testid="hm-report-date-from"
            />
            <span className="text-text-tertiary">—</span>
            <DatePicker
              value={params.dateTo}
              onChange={(value) => set({ preset: 'custom', dateTo: value })}
              aria-label={t('tenant.handelMagazyn.filters.dateTo')}
              data-testid="hm-report-date-to"
            />
          </div>
        </section>

        <section className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="mb-1 block text-text-secondary">
              {t('tenant.handelMagazyn.sales.columns.buyer')}
            </span>
            <Dropdown
              value={params.buyerId != null ? String(params.buyerId) : ''}
              onChange={(value) => set({ buyerId: value ? Number(value) : null })}
              placeholder={t('tenant.handelMagazyn.reports.allContractors')}
              options={[
                { value: '', label: t('tenant.handelMagazyn.reports.allContractors') },
                ...(contractors.data?.items ?? []).map((c) => ({
                  value: String(c.id),
                  label: c.name,
                })),
              ]}
              data-testid="hm-report-buyer"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-text-secondary">
              {t('tenant.handelMagazyn.columns.status')}
            </span>
            <Dropdown
              value={params.status ?? ''}
              onChange={(value) => set({ status: (value as 'buffer' | 'issued') || null })}
              options={[
                { value: '', label: t('tenant.handelMagazyn.reports.allStatuses') },
                { value: 'issued', label: t('tenant.handelMagazyn.status.issued') },
                { value: 'buffer', label: t('tenant.handelMagazyn.status.buffer') },
              ]}
              data-testid="hm-report-status"
            />
          </label>
        </section>

        <section className="space-y-2">
          <label className="block text-sm">
            <span className="mb-1 block text-text-secondary">
              {t('tenant.handelMagazyn.reports.summarizeBy')}
            </span>
            <Dropdown
              value={params.dim}
              onChange={(value) => set({ dim: value as SalesReportParams['dim'] })}
              options={dimensionOptions}
              data-testid="hm-report-dimension"
            />
          </label>
          <Checkbox
            checked={params.details}
            onChange={(e) => toggleDetails(e.target.checked)}
            label={t('tenant.handelMagazyn.reports.withDetails')}
            hint={t('tenant.handelMagazyn.reports.withDetailsHint')}
            data-testid="hm-report-details"
          />
          <Checkbox
            checked={params.positions}
            disabled={!params.details}
            onChange={(e) => set({ positions: e.target.checked })}
            label={t('tenant.handelMagazyn.reports.withPositions')}
            hint={t('tenant.handelMagazyn.reports.withPositionsHint')}
            data-testid="hm-report-positions"
          />
        </section>
      </div>
    </Modal>
  );
}
